import { CameraOutlined } from '@ant-design/icons'
import { Alert, Button, Form, Input, Select, Skeleton, Switch, Tooltip, message } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../AuthContext'
import { useWorkspaceState } from '../WorkspaceContext'
import { getTeamSettings, patchTeamSettings, teamErrorMessage } from './teamApi'
import type { TeamSettings, TeamSettingsPatch } from './teamTypes'
import { TeamAvatar, useTeamFormat } from './teamUi'

const MAX_AVATAR_CHARS = 200_000
const AVATAR_PX = 256

/** Centre-crop to a square, downscale and re-encode until it fits the server cap. */
async function fileToAvatarDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file)
  const canvas = document.createElement('canvas')
  canvas.width = AVATAR_PX
  canvas.height = AVATAR_PX
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas unavailable')
  const side = Math.min(bitmap.width, bitmap.height)
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, AVATAR_PX, AVATAR_PX)
  bitmap.close?.()
  for (const [type, q] of [
    ['image/webp', 0.9],
    ['image/webp', 0.75],
    ['image/jpeg', 0.8],
    ['image/jpeg', 0.6],
  ] as const) {
    const url = canvas.toDataURL(type, q)
    if (url.startsWith(`data:${type}`) && url.length <= MAX_AVATAR_CHARS) return url
  }
  throw new Error('too large')
}

function SoonTag() {
  const { t } = useTranslation('team')
  return <span className="tm-soon">{t('settings.soon')}</span>
}

/**
 * VistoeraTASK-00525 — 团队设置. Only fields that really persist are editable
 * (name / description / avatar for Owner+Admin, company info for the Owner).
 * Quota / credit-cap / dissolve are shown disabled and labelled — never faked.
 */
export default function TeamSettingsTab({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation('team')
  const { dateTime, num } = useTeamFormat()
  const { reload: reloadWorkspace } = useWorkspaceState()
  const { refresh: refreshAuth } = useAuth()
  const [data, setData] = useState<TeamSettings | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [avatar, setAvatar] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [form] = Form.useForm()

  const apply = useCallback(
    (s: TeamSettings) => {
      setData(s)
      setAvatar(s.profile.avatar_data_url)
      form.setFieldsValue({
        name: s.workspace.name,
        description: s.profile.description ?? '',
        company_name: s.profile.company_name ?? '',
        contact_name: s.profile.contact_name ?? '',
        contact_email: s.profile.contact_email ?? '',
        contact_phone: s.profile.contact_phone ?? '',
        company_address: s.profile.company_address ?? '',
      })
    },
    [form],
  )

  useEffect(() => {
    let cancelled = false
    getTeamSettings(workspaceId)
      .then((res) => !cancelled && apply(res.data))
      .catch((e) => !cancelled && setError(teamErrorMessage(e, t('settings.loadFailed'))))
    return () => {
      cancelled = true
    }
  }, [workspaceId, apply, t])

  const onPickAvatar = async (file?: File) => {
    if (!file) return
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
      void message.error(t('settings.avatarType'))
      return
    }
    try {
      setAvatar(await fileToAvatarDataUrl(file))
    } catch {
      void message.error(t('settings.avatarTooLarge'))
    }
  }

  const save = async () => {
    if (!data) return
    const v = await form.validateFields()
    const patch: TeamSettingsPatch = {}
    if (v.name.trim() !== data.workspace.name) patch.name = v.name.trim()
    if ((v.description ?? '').trim() !== (data.profile.description ?? '')) patch.description = (v.description ?? '').trim() || null
    if (avatar !== data.profile.avatar_data_url) patch.avatar_data_url = avatar
    if (data.permissions.can_edit_company) {
      for (const k of ['company_name', 'contact_name', 'contact_email', 'contact_phone', 'company_address'] as const) {
        const next = (v[k] ?? '').trim()
        if (next !== (data.profile[k] ?? '')) patch[k] = next || null
      }
    }
    if (!Object.keys(patch).length) {
      void message.info(t('settings.noChanges'))
      return
    }
    setSaving(true)
    try {
      const res = await patchTeamSettings(workspaceId, patch)
      apply(res.data)
      void message.success(t('settings.saved'))
      // Name / avatar are shown in the shell: re-read the Workspace + session so every surface agrees.
      reloadWorkspace()
      void refreshAuth()
    } catch (e) {
      void message.error(teamErrorMessage(e, t('settings.saveFailed')))
    } finally {
      setSaving(false)
    }
  }

  if (error && !data) return <Alert type="error" showIcon title={error} />
  if (!data) return <Skeleton active paragraph={{ rows: 10 }} />

  const canCompany = data.permissions.can_edit_company
  const info = data.team_info

  return (
    <div className="tm-settings" data-testid="team-settings">
      <Form form={form} layout="vertical" autoComplete="off">
        <section className="tm-card tm-settings-card">
          <h3 className="tm-card-title">{t('settings.basic.title')}</h3>
          <div className="tm-basic">
            <div className="tm-avatar-edit">
              <TeamAvatar src={avatar} name={form.getFieldValue('name') || data.workspace.name} size={88} />
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => {
                  void onPickAvatar(e.target.files?.[0])
                  e.target.value = ''
                }}
                data-testid="team-avatar-input"
              />
              <Button size="small" icon={<CameraOutlined />} onClick={() => fileRef.current?.click()}>
                {t('settings.basic.changeAvatar')}
              </Button>
              {avatar && (
                <Button size="small" type="link" onClick={() => setAvatar(null)}>
                  {t('settings.basic.removeAvatar')}
                </Button>
              )}
              <div className="tm-hint">{t('settings.basic.avatarHint')}</div>
            </div>
            <div className="tm-basic-fields">
              <Form.Item
                name="name"
                label={t('settings.basic.name')}
                rules={[
                  { required: true, whitespace: true, message: t('settings.basic.nameRequired') },
                  { max: 100, message: t('create.nameTooLong') },
                ]}
              >
                <Input maxLength={100} data-testid="team-settings-name" />
              </Form.Item>
              <Form.Item name="description" label={t('settings.basic.description')}>
                <Input.TextArea rows={3} maxLength={400} showCount />
              </Form.Item>
            </div>
          </div>
        </section>

        <section className="tm-card tm-settings-card">
          <h3 className="tm-card-title">{t('settings.info.title')}</h3>
          <dl className="tm-dl tm-dl-wide">
            <dt>{t('settings.info.id')}</dt>
            <dd>
              <code>{info.workspace_id}</code>
            </dd>
            <dt>{t('settings.info.createdAt')}</dt>
            <dd>{dateTime(info.created_at)}</dd>
            <dt>{t('settings.info.type')}</dt>
            <dd>{t('settings.info.typeTeam')}</dd>
            <dt>{t('settings.info.members')}</dt>
            <dd>{t('settings.info.memberLine', { total: info.member_total, active: info.member_active })}</dd>
            <dt>{t('settings.info.credits')}</dt>
            <dd>{info.credit_balance === null ? t('overview.noData') : num(info.credit_balance)}</dd>
            <dt>{t('settings.info.plan')}</dt>
            <dd>{info.plan.name ?? t('settings.info.noPlan')}</dd>
          </dl>
        </section>

        <section className="tm-card tm-settings-card">
          <h3 className="tm-card-title">
            {t('settings.company.title')}
            {!canCompany && <span className="tm-soon">{t('settings.company.ownerOnly')}</span>}
          </h3>
          <div className="tm-grid-2">
            <Form.Item name="company_name" label={t('settings.company.name')}>
              <Input maxLength={120} disabled={!canCompany} />
            </Form.Item>
            <Form.Item name="contact_name" label={t('settings.company.contact')}>
              <Input maxLength={80} disabled={!canCompany} />
            </Form.Item>
            <Form.Item name="contact_email" label={t('settings.company.email')}>
              <Input maxLength={160} disabled={!canCompany} />
            </Form.Item>
            <Form.Item name="contact_phone" label={t('settings.company.phone')}>
              <Input maxLength={40} disabled={!canCompany} />
            </Form.Item>
          </div>
          <Form.Item name="company_address" label={t('settings.company.address')}>
            <Input maxLength={240} disabled={!canCompany} />
          </Form.Item>
        </section>

        <div className="tm-save-bar">
          <Button type="primary" loading={saving} onClick={() => void save()} data-testid="team-settings-save">
            {t('settings.save')}
          </Button>
        </div>
      </Form>

      <section className="tm-card tm-settings-card">
        <h3 className="tm-card-title">{t('settings.defaults.title')}</h3>
        <dl className="tm-dl tm-dl-wide">
          <dt>{t('settings.defaults.role')}</dt>
          <dd>{t('role.member')}</dd>
          <dt>{t('settings.defaults.visibility')}</dt>
          <dd>{t('settings.defaults.visibilityValue')}</dd>
        </dl>
        <div className="tm-hint">{t('settings.defaults.readonly')}</div>
      </section>

      <section className="tm-card tm-settings-card tm-disabled-card">
        <h3 className="tm-card-title">
          {t('settings.quota.title')}
          <SoonTag />
        </h3>
        <div className="tm-grid-2">
          <div>
            <div className="tm-field-label">{t('settings.quota.monthly')}</div>
            <Input disabled placeholder={t('settings.quota.unlimited')} />
          </div>
          <div>
            <div className="tm-field-label">{t('settings.quota.reset')}</div>
            <Select disabled style={{ width: '100%' }} placeholder={t('settings.quota.resetMonthly')} />
          </div>
          <div>
            <div className="tm-field-label">{t('settings.quota.overLimit')}</div>
            <Select disabled style={{ width: '100%' }} placeholder={t('settings.quota.overLimitBlock')} />
          </div>
          <div>
            <div className="tm-field-label">{t('settings.quota.allowCreate')}</div>
            <Switch disabled />
          </div>
        </div>
        <div className="tm-hint">{t('settings.quota.note')}</div>
      </section>

      <section className="tm-card tm-settings-card tm-danger-card">
        <h3 className="tm-card-title">{t('settings.danger.title')}</h3>
        <div className="tm-danger-row">
          <div>
            <div>{t('settings.danger.dissolve')}</div>
            <div className="tm-hint">{t('settings.danger.dissolveDesc')}</div>
          </div>
          <Tooltip title={t('settings.danger.unsupported')}>
            <span>
              <Button danger disabled data-testid="team-dissolve">
                {t('settings.danger.dissolve')}
              </Button>
            </span>
          </Tooltip>
        </div>
      </section>
    </div>
  )
}
